import { ApiRequestConfig, apiService } from './api.service';

import { IStripeAccountStatus } from '@/components/stripe/StripeConnectButton';
import { HaggleType } from '@/components/utils/enums';
import { IChat } from '@/interfaces/api/IChat';
import { ICollectionChat } from '@/interfaces/api/ICollectionChat';
import { ICollectionPrompt } from '@/interfaces/api/ICollectionPrompt';
import { IPrompt } from '@/interfaces/api/IPrompt';
import { IStore, IStoreForOAuth } from '@/interfaces/api/IStore';
import { IUser } from '@/interfaces/api/IUser';
import { InventoryType } from '@/interfaces/api/InventoryType';
import { ChatBotResponseAi } from '@/interfaces/api/chat/ChatBotResponseAi';
import { ChatBotSessionResponseDto } from '@/interfaces/api/chat/ChatBotSessionResponseDto';
import { ICreateChatPayload } from '@/interfaces/api/chat/ICreateChatPayload';
import { ICollection } from '@/interfaces/api/collection/ICollection';
import { ICollectionHaggleEvent } from '@/interfaces/api/haggle-event/ICollectionHaggleEvent';
import { ICreateHaggleEventPayload } from '@/interfaces/api/haggle-event/ICreateHaggleEventPayload';
import { IHaggleEvent } from '@/interfaces/api/haggle-event/IHaggleEvent';
import {
	IHaggleEventMonthlySalesReport,
	IHaggleEventSalesReport,
} from '@/interfaces/api/haggle-event/IHaggleEventSalesReport';
import { VendorHaggleSummary } from '@/interfaces/api/haggle-event/IVendorHaggleSummary';
import { HaggleInvite } from '@/interfaces/api/haggle-invite/haggleInvite';
import {
	ICollectionHaggleInvite,
	ICollectionInvite,
} from '@/interfaces/api/invite/IInvite';
import { ICollectionMerchant } from '@/interfaces/api/merchant/ICollectionMerchant';
import { INotifications } from '@/interfaces/api/notifications/INotifications';
import { ICreateOrder } from '@/interfaces/api/orders/CreateOrderDto';
import { IOrder } from '@/interfaces/api/orders/IOrder';
import { ICollectionProduct } from '@/interfaces/api/product/ICollectionProduct';
import { ICreateProductPayload } from '@/interfaces/api/product/ICreateProductPayload';
import { IHaggleFilters } from '@/interfaces/api/product/IHaggleFilters';
import { IProduct } from '@/interfaces/api/product/IProduct';
import { ICollectionProductId } from '@/interfaces/api/product/IProductIdWithHaggle';
import { ICreatePromptPayload } from '@/interfaces/api/prompt/ICreatePromptPayload';
import { ICreateShopifySubscriptionDiscountPayload } from '@/interfaces/api/shopify-subscription-discount/ICreateShopifySubscriptionDiscountPayload';
import { IShopifySubscriptionDiscount } from '@/interfaces/api/shopify-subscription-discount/IShopifySubscriptionDiscount';
import { IUpdateShopifySubscriptionDiscountPayload } from '@/interfaces/api/shopify-subscription-discount/IUpdateShopifySubscriptionDiscountPayload';
import { ICollectionStore } from '@/interfaces/api/store/ICollectionStore';
import { IStorePublic } from '@/interfaces/api/store/IStorePublic';
import { IUpdateStorePayload } from '@/interfaces/api/store/IUpdateStorePayload';
import {
	ICreatePromotionalUserPayload,
	IPromotionalUser,
} from '@/interfaces/api/user/IPromotionalUser';
import { IUpdateUserPayload } from '@/interfaces/api/user/IUpdateUserPayload';
import { IUpdateUserProfilePayload } from '@/interfaces/api/user/IUpdateUserProfilePayload';
import { IUserAssignRole } from '@/interfaces/api/user/IUserAssignRole';
import { ICreateStoreResponse } from '@/interfaces/haggle/store/ICreateStoreResponse';
import { IContactForm } from '@/interfaces/notifications/ContactForm';
import { IApiService } from '@/interfaces/services/IApiService';
import { IHaggleApiService } from '@/interfaces/services/IHaggleApiService';

class HaggleApiService implements IHaggleApiService {
	api: IApiService<ApiRequestConfig>;
	constructor(api: IApiService<ApiRequestConfig>) {
		this.api = api;
	}

	async createStore(
		name: string,
		tradeName: string,
		accessToken: string,
		config?: ApiRequestConfig,
	): Promise<IStore> {
		return await this.api.post<ICreateStoreResponse>(
			'/store',
			{ name, tradeName, accessToken },
			config,
		);
	}

	async getUserStore(
		excludeEvents?: boolean,
		config?: ApiRequestConfig,
	): Promise<IStore> {
		const mergedConfig: ApiRequestConfig = {
			...config,
			params: {
				...(config?.params || {}),
				...(excludeEvents ? { excludeEvents: true } : {}),
			},
		};

		return await this.api.get<IStore>('/store/user', mergedConfig);
	}

	async getUserStoreForOAuth(
		excludeEvents?: boolean,
		config?: ApiRequestConfig,
	): Promise<IStoreForOAuth> {
		const mergedConfig: ApiRequestConfig = {
			...config,
			params: {
				...(config?.params || {}),
				...(excludeEvents ? { excludeEvents: true } : {}),
			},
		};

		return await this.api.get<{ store: IStore | null; hasStore: boolean }>(
			'/store/user/oauth',
			mergedConfig,
		);
	}

	async getStoreByName(
		name?: string,
		config?: ApiRequestConfig,
	): Promise<IStore> {
		return await this.api.get<IStore>(`/store/name/${name}`, config);
	}

	async getStoreThemeByName(
		name?: string,
		config?: ApiRequestConfig,
	): Promise<IStore> {
		return await this.api.get<IStore>(`/store/theme/${name}`, config);
	}

	async getStoreByTradeName(
		tradeName?: string,
		config?: ApiRequestConfig,
	): Promise<IStore> {
		return await this.api.get<IStore>(`/store/trade-name/${tradeName}`, config);
	}

	async updateStore(
		id: number,
		payload: IUpdateStorePayload,
		config?: ApiRequestConfig,
	): Promise<IStore> {
		return await this.api.patch<IStore>(`/store/${id}`, { ...payload }, config);
	}

	async getMe(config?: ApiRequestConfig): Promise<IUser> {
		return await this.api.get<IUser>('/user/me', config);
	}

	async getUserPlan(email: string, config?: ApiRequestConfig): Promise<any> {
		return await this.api.get<any>(
			`subscriptions/subscription-details?email=${encodeURIComponent(email)}`,
			config,
		);
	}

	async getUserPlanShopify(
		shopName: string,
		config?: ApiRequestConfig,
	): Promise<any> {
		return await this.api.get<any>(
			`store/subscription/details?storeName=${shopName}`,
			config,
		);
	}

	async getAllInvites({
		params,
	}: {
		params: Record<string, any>;
	}): Promise<ICollectionInvite> {
		const data = this.api.get<ICollectionInvite>('/invites', { params });
		return data;
	}

	async getHaggleAllInvites({
		params,
	}: {
		params: Record<string, any>;
	}): Promise<ICollectionHaggleInvite> {
		const data = this.api.get<ICollectionHaggleInvite>('/haggle-invites', {
			params,
		});
		return data;
	}

	async updateHaggleInviteStatus(
		id: number,
		dto: { status: string; haggleEventId: number },
	) {
		return this.api.patch(`/haggle-invites/${id}`, dto);
	}

	async inviteMerchant(body: {
		invitedEmail: string;
		adminEmail: string;
		name: string;
	}) {
		return await this.api.post('user/invite-merchant', body);
	}

	async getUserTransactions(
		email: string,
		limit = 10,
		startingAfter?: string,
		config?: ApiRequestConfig,
	): Promise<any> {
		return await this.api.get<any>(
			`subscriptions/transaction-history?email=${encodeURIComponent(
				email,
			)}&limit=${limit}${
				startingAfter ? `&startingAfter=${startingAfter}` : ''
			}`,
			config,
		);
	}

	async getMonthlyReport(
		email: string,
		month: string,
		year: string,
		config?: ApiRequestConfig,
	): Promise<Blob> {
		const response = await this.api.get<Blob>(
			`/subscriptions/monthly-report?email=${encodeURIComponent(
				email,
			)}&month=${month}&year=${year}`,
			{ ...config, responseType: 'blob' },
		);
		return response;
	}

	async getSelectedInvoice(
		email: string,
		invoiceId: string,
		config?: ApiRequestConfig,
	): Promise<any> {
		return await this.api.get<any>(
			`subscriptions/download-invoice?email=${encodeURIComponent(
				email,
			)}&invoiceId=${invoiceId}`,
			config,
		);
	}

	async manageSubscription(
		payload: any,
		config?: ApiRequestConfig,
	): Promise<any> {
		return await this.api.post<any>(
			'/subscriptions/manage-subscription',
			payload,
			config,
		);
	}

	async manageSubscriptionShopify(
		payload: any,
		config?: ApiRequestConfig,
	): Promise<any> {
		return await this.api.post<any>('store/subscription', payload, config);
	}

	async updateRole(payload: any, config?: ApiRequestConfig): Promise<any> {
		return await this.api.patch<any>(
			'/subscriptions/update-role',
			payload,
			config,
		);
	}

	async updateRoleShopify(
		payload: any,
		config?: ApiRequestConfig,
	): Promise<any> {
		return await this.api.patch<any>('/store/update/role', payload, config);
	}

	async updateCardDetails(
		payload: any,
		config?: ApiRequestConfig,
	): Promise<any> {
		return await this.api.patch<any>(
			'/subscriptions/card-details',
			payload,
			config,
		);
	}

	async getAllPrompts(config?: ApiRequestConfig): Promise<ICollectionPrompt> {
		return await this.api.get<ICollectionPrompt>('/prompt/', config);
	}

	async createPrompt(
		payload: ICreatePromptPayload,
		config?: ApiRequestConfig,
	): Promise<IPrompt> {
		return await this.api.post<IPrompt>(
			'/prompt',
			{ ...payload, isDefault: payload.isDefault === 'Yes' ? true : false },
			config,
		);
	}

	async updatePrompt(
		id: number,
		payload: ICreatePromptPayload,
		config?: ApiRequestConfig,
	): Promise<IPrompt> {
		return await this.api.patch<IPrompt>(
			`/prompt/${id}`,
			{ ...payload, isDefault: payload.isDefault === 'Yes' ? true : false },
			config,
		);
	}

	async getPromptById(id: number, config?: ApiRequestConfig): Promise<IPrompt> {
		return await this.api.get<IPrompt>(`/prompt/${id}`, config);
	}

	async deletePromptById(id: number, config?: ApiRequestConfig): Promise<void> {
		return await this.api.delete(`/prompt/${id}`, config);
	}

	async getDefaultPrompt(config?: ApiRequestConfig): Promise<IPrompt> {
		return await this.api.get<IPrompt>('/prompt/default', config);
	}

	async createHaggleEvent(
		payload: ICreateHaggleEventPayload,
		config?: ApiRequestConfig,
	): Promise<IHaggleEvent> {
		return await this.api.post<IHaggleEvent>(
			'/haggle-event',
			{
				...payload,
				productIds: payload.productIds.map((product) => product.id.toString()),
				productDetails: payload.productIds.map((product) => ({
					id: product.id.toString(),
					product_type: product.product_type,
					countryName: product.store?.country_name,
					prices: product.variants.map((variant) => variant.price),
					variants: product.variants.map((variant) => ({
						id: variant.id.toString(),
						price: variant.price,
						inventoryQuantity: variant.inventory_quantity,
					})),
				})),
				haggleType: payload.haggleType,
				password: [HaggleType.PRIVATE, HaggleType.SEMI_PRIVATE].includes(
					payload.haggleType as HaggleType,
				)
					? payload.password
					: undefined,
			},
			config,
		);
	}

	async createHaggleInvite(payload: {
		merchantId: number | undefined;
		haggleEventId: number;
		invitedEmails?: string[] | undefined | null;
		haggleType: HaggleType;
		haggleName?: string;
		password?: string | null;
	}): Promise<any> {
		return await this.api.post('/haggle-invites', payload);
	}
	async updateHaggleEvent(
		id: number,
		payload: ICreateHaggleEventPayload,
		config?: ApiRequestConfig,
	): Promise<IHaggleEvent> {
		return await this.api.patch<IHaggleEvent>(
			`/haggle-event/${id}`,
			{
				...payload,
				productIds: payload.productIds.map((product) => product.id.toString()),
				productDetails: payload.productIds.map((product) => ({
					id: product.id.toString(),
					product_type: product.product_type,
					countryName: product.store?.country_name,
					prices: product.variants.map((variant) => variant.price),
					variants: product.variants.map((variant) => ({
						id: variant.id.toString(),
						price: variant.price,
						inventoryQuantity: variant.inventory_quantity,
					})),
				})),
			},
			config,
		);
	}

	async getHaggleEventById(
		id: number,
		config?: ApiRequestConfig,
	): Promise<IHaggleEvent> {
		return await this.api.get<IHaggleEvent>(
			`/haggle-event/${id}/with-products`,
			config,
		);
	}
	async getAllHaggleEvents(
		config?: ApiRequestConfig,
	): Promise<ICollectionHaggleEvent> {
		return await this.api.get<ICollectionHaggleEvent>('/haggle-event/', config);
	}

	async getHaggleEventSalesReport(
		id: number,
		config?: ApiRequestConfig,
	): Promise<IHaggleEventSalesReport> {
		return await this.api.get<IHaggleEventSalesReport>(
			`/haggle-event/sales-report/${id}`,
			config,
		);
	}

	async getHaggleEventMonthlySalesReportAdmin(
		date: string,
		config?: ApiRequestConfig,
	): Promise<IHaggleEventMonthlySalesReport> {
		return await this.api.get<IHaggleEventMonthlySalesReport>(
			`/haggle-event/admin/monthly-sales-report/${date}`,
			config,
		);
	}

	async getHaggleEventMonthlySalesReport(
		date: string,
		config?: ApiRequestConfig,
	): Promise<IHaggleEventMonthlySalesReport> {
		return await this.api.get<IHaggleEventMonthlySalesReport>(
			`/haggle-event/monthly-sales-report/${date}`,
			config,
		);
	}

	async getAllHaggleEventByProductId(
		config?: ApiRequestConfig,
	): Promise<ICollectionHaggleEvent> {
		return await this.api.get<ICollectionHaggleEvent>(
			'/haggle-event/public',
			config,
		);
	}

	async deleteHaggleEvent(
		id: number,
		config?: ApiRequestConfig,
	): Promise<void> {
		return await this.api.delete(`/haggle-event/${id}`, config);
	}

	async getAllStoreProducts(
		config?: ApiRequestConfig,
	): Promise<ICollectionProduct> {
		const response = await this.api.get<ICollectionProduct>('/store/products', {
			...config,
			params: {
				...config?.params,
				status: 'active',
			},
		});
		return response;
	}

	async getProductById(
		id: string,
		config?: ApiRequestConfig,
	): Promise<IProduct> {
		const response = await this.api.get<IProduct>(`/store/products/${id}`, {
			...config,
		});
		return response;
	}

	async getAllStoreProductsWithHaggle(
		config?: ApiRequestConfig,
	): Promise<ICollectionProductId> {
		return await this.api.get<ICollectionProductId>(
			'/haggle-event/store/products/',
			config,
		);
	}

	async getStoreProductsWithHaggleByUserId(
		userId: number,
		config?: ApiRequestConfig,
	): Promise<ICollectionProduct> {
		return await this.api.get<ICollectionProduct>(
			`/haggle-event/store/products/by-user/${userId}`,
			config,
		);
	}

	async hasStoreProducts(
		config?: ApiRequestConfig,
	): Promise<{ hasProducts: boolean }> {
		return await this.api.get<{ hasProducts: boolean }>(
			'/haggle-event/store/has-products',
			config,
		);
	}

	async getAllProductsByStoreName(
		storeName: string,
		config?: ApiRequestConfig,
	): Promise<IProduct[]> {
		return await this.api.get<IProduct[]>(
			`/store/public/products/${storeName}`,
			config,
		);
	}

	async getPublicProductById(
		productId?: string,
		storeName?: string,
		config?: ApiRequestConfig,
	): Promise<IProduct> {
		return await this.api.get<IProduct>(
			`/store/public/product/${productId}/store/${storeName}`,
			config,
		);
	}

	async getAllProducts(config?: ApiRequestConfig): Promise<ICollectionProduct> {
		return await this.api.get<ICollectionProduct>(`/products`, config);
	}

	async bulkCreateProducts(
		payload: ICreateProductPayload[],
		config?: ApiRequestConfig,
	): Promise<IProduct[]> {
		return await this.api.post<IProduct[]>(
			'/products/bulk',
			{ products: payload },
			config,
		);
	}

	async getChatById(id: number, config?: ApiRequestConfig): Promise<IChat> {
		return await this.api.get<IChat>(`/chat-bot/${id}`, config);
	}

	async getUserChats(config?: ApiRequestConfig): Promise<ICollectionChat> {
		return await this.api.get<ICollectionChat>('/chat-bot', config);
	}

	async createChat(
		payload: ICreateChatPayload,
		config?: ApiRequestConfig,
	): Promise<ChatBotSessionResponseDto> {
		return await this.api.post<ChatBotSessionResponseDto>(
			'/chat-bot/create-chat',
			payload,
			config,
		);
	}

	async createLocalChat(
		payload: ICreateChatPayload,
		config?: ApiRequestConfig,
	): Promise<ChatBotSessionResponseDto> {
		return await this.api.post<ChatBotSessionResponseDto>(
			'/chat-bot/create-local-chat',
			payload,
			config,
		);
	}

	async sendMessage(
		message: string,
		sessionId: string,
		config?: ApiRequestConfig,
	): Promise<ChatBotResponseAi> {
		return await this.api.post<ChatBotResponseAi>(
			'/chat-bot/send-message',
			{ message, sessionId },
			config,
		);
	}

	async sendLocalMessage(
		message: string,
		sessionId: string,
		config?: ApiRequestConfig,
	): Promise<ChatBotResponseAi> {
		return await this.api.post<ChatBotResponseAi>(
			'/chat-bot/send-local-message',
			{ message, sessionId },
			config,
		);
	}

	async inviteUser(email: string, name: string, config?: ApiRequestConfig) {
		return await this.api.post<IUser>('store/invite', { email, name }, config);
	}

	async inviteMerchantUser(
		email: string,
		name: string,
		config?: ApiRequestConfig,
	) {
		return await this.api.post<IUser>(
			'user/test-email',
			{ email, name },
			config,
		);
	}

	async removeUserFromStore(userId: number, config?: ApiRequestConfig) {
		return await this.api.patch(`/store/remove-user/${userId}`, config);
	}

	async getAllHaggleEventsAdmin(
		config?: ApiRequestConfig,
	): Promise<ICollectionHaggleEvent> {
		return await this.api.get<ICollectionHaggleEvent>(
			'/haggle-event/admin/',
			config,
		);
	}

	async getAllMerchants(
		config?: ApiRequestConfig,
	): Promise<ICollectionMerchant> {
		return await this.api.get<ICollectionMerchant>('/user/', config);
	}

	async getAllStores(config?: ApiRequestConfig): Promise<ICollectionStore> {
		return await this.api.get<ICollectionStore>('/store/', config);
	}

	async getUserById(id?: string, config?: ApiRequestConfig): Promise<IUser> {
		return await this.api.get<IUser>(`/user/${id}`, config);
	}

	async getUserByEmail(email: string): Promise<IUser> {
		return await this.api.get<IUser>(`/user/email/${email}`);
	}

	async assignRole(
		id: string,
		payload: IUserAssignRole,
		config?: ApiRequestConfig,
	): Promise<IUser> {
		return await this.api.patch<IUser>(`/user/${id}/roles`, payload, config);
	}

	async getVariantTitleByVariantId(
		variantId: string,
		storeName: string,
		config?: ApiRequestConfig,
	): Promise<{ title: string }> {
		return await this.api.get(
			`/store/public/${storeName}/variant/${variantId}`,
			config,
		);
	}

	async updateUser(payload: IUpdateUserPayload, config?: ApiRequestConfig) {
		return await this.api.patch<IUpdateUserPayload>(
			'/user/me',
			payload,
			config,
		);
	}

	async getAllHaggleProducts(
		config?: ApiRequestConfig,
	): Promise<ICollectionProduct> {
		return await this.api.get<ICollectionProduct>(
			'/haggle-event/public/products/',
			config,
		);
	}

	async getAllHaggleVendors(
		config?: ApiRequestConfig,
	): Promise<VendorHaggleSummary[]> {
		return await this.api.get<VendorHaggleSummary[]>(
			'/haggle-event/public/vendors-with-haggles/',
			config,
		);
	}

	async checkProductInHaggle(
		productId: string,
		config?: ApiRequestConfig,
	): Promise<{
		inHaggle: boolean;
		customReturnAndExchangePolicy?: string;
		shippingPolicy?: string;
	}> {
		return await this.api.get<{
			inHaggle: boolean;
			customReturnAndExchangePolicy?: string;
			shippingPolicy?: string;
		}>('/haggle-event/product/' + productId, config);
	}

	async updateInventoryType(
		payload: { inventoryType: InventoryType },
		config?: ApiRequestConfig,
	): Promise<any> {
		return await this.api.patch<any>('/user/inventory-type', payload, config);
	}

	async updateBusinessName(
		payload: { businessName: string },
		config?: ApiRequestConfig,
	): Promise<IUser> {
		return await this.api.patch<IUser>('/user/business-name', payload, config);
	}

	async createStoreWithUserDetails(
		payload: {
			name: string;
			tradeName: string;
			accessToken: string;
			inventoryType?: string;
			businessName?: string;
		},
		config?: ApiRequestConfig,
	): Promise<IStore> {
		return await this.api.post<IStore>(
			'/store/with-user-details',
			payload,
			config,
		);
	}

	async getProductByIdAndStoreName(
		storeName: string,
		productId: string,
		isCSVHaggle?: boolean,
		config?: ApiRequestConfig,
	): Promise<IProduct> {
		return await this.api.get<IProduct>(
			`/haggle-event/store/${storeName}/product/${productId}?isCSVHaggle=${isCSVHaggle}`,
			config,
		);
	}

	async getAllStoresPublic(config?: ApiRequestConfig): Promise<IStorePublic[]> {
		return await this.api.get<IStorePublic[]>('/store/public', config);
	}

	async getAllHaggleStoredFilters(
		config?: ApiRequestConfig,
	): Promise<IHaggleFilters> {
		return await this.api.get<IHaggleFilters>(
			'/haggle-event/public/filters',
			config,
		);
	}

	async updateUserProfile(
		payload: IUpdateUserProfilePayload,
		config?: ApiRequestConfig,
	) {
		return await this.api.patch<IUpdateUserProfilePayload>(
			'/user/update-profile',
			payload,
			config,
		);
	}

	async updateMerchantInfo(
		payload: IUpdateUserProfilePayload,
		config?: ApiRequestConfig,
	) {
		return await this.api.patch<IUpdateUserProfilePayload>(
			'/store/merchant/update-merchant-info',
			payload,
			config,
		);
	}

	async getNotifications(
		email: string,
		config?: ApiRequestConfig,
	): Promise<INotifications[]> {
		return await this.api.get<INotifications[]>(
			`notifications?email=${encodeURIComponent(email)}`,
			config,
		);
	}

	async getAllNotifications(
		config?: ApiRequestConfig,
	): Promise<ICollection<INotifications>> {
		return await this.api.get<ICollection<INotifications>>(
			'/notifications/all',
			config,
		);
	}

	async getFavourites(
		config?: ApiRequestConfig,
	): Promise<ICollection<INotifications>> {
		return await this.api.get<ICollection<INotifications>>(
			'/notifications/favourites',
			config,
		);
	}
	async createNotification(
		email: string,
		message: string,
		sent: boolean,
		haggleStartDate: string,
		sendAt: string,
		haggleEventId: number,
		productId: string,
		productName: string,
		productUrl: string,
		productImage: string,
		isFavourite?: boolean,
		favouritedUserId?: string,
		config?: ApiRequestConfig,
	): Promise<INotifications> {
		return await this.api.post<INotifications>(
			'/notifications',
			{
				email,
				message,
				sent,
				haggleStartDate,
				sendAt,
				haggleEventId,
				productId,
				productName,
				productUrl,
				productImage,
				isFavourite,
				favouritedUserId,
			},
			config,
		);
	}

	async getFavouritedNotifications(
		config?: ApiRequestConfig,
	): Promise<INotifications[]> {
		return this.api.get('/notifications/user-favourites', config);
	}

	async getHaggleInvitesByEmail(
		email: string,
		config?: ApiRequestConfig,
	): Promise<HaggleInvite[]> {
		return this.api.get(
			`/haggle-invites/by-email?email=${encodeURIComponent(email)}`,
			config,
		);
	}

	async deleteFavouriteNotificationById(
		favouritedUserId?: string,
		config?: ApiRequestConfig,
	): Promise<void> {
		return await this.api.delete(
			`/notifications/remove-favourite/${favouritedUserId}`,
			config,
		);
	}

	async markEventAsFavourite(
		body: {
			startDate: string;
			endDate: string;
			username: string;
		},
		config?: ApiRequestConfig,
	): Promise<void> {
		return await this.api.post('/notifications/send-favourite', body, config);
	}

	async deleteNotificationById(
		id: number,
		config?: ApiRequestConfig,
	): Promise<void> {
		return await this.api.delete(`/notifications/${id}`, config);
	}

	async getMerchantHaggleChatCount(): Promise<{ count: number }> {
		return await this.api.get('/chat-bot/count/merchant');
	}
	async sendContactEmail(body: IContactForm): Promise<ChatBotResponseAi> {
		return await this.api.post('/notifications/send-contact-email', body);
	}

	async getPlans(): Promise<any> {
		const values = await this.api.get('/config/all');
		return values;
	}

	async updateConfig(name: string, payload: any, config?: ApiRequestConfig) {
		return await this.api.patch(`/config/${name}`, payload, config);
	}

	async updateChatbotSettings(
		storeId: number,
		data: {
			themeType?: string | null;
			theme?: string | null;
			businessLogo?: File | null;
			avatar?: File | null;
			avatarUrl?: string;
			resetLogo?: boolean;
			resetAvatar?: boolean;
			resetTheme?: boolean;
		},
		config?: ApiRequestConfig,
	): Promise<IStore> {
		const formData = new FormData();

		if (data.themeType) formData.append('themeType', data.themeType);
		if (data.theme) formData.append('theme', data.theme);
		if (data.resetLogo) formData.append('resetLogo', String(data.resetLogo));
		if (data.resetAvatar)
			formData.append('resetAvatar', String(data.resetAvatar));
		if (data.resetTheme) formData.append('resetTheme', String(data.resetTheme));
		if (data.businessLogo)
			formData.append('files', data.businessLogo, 'businessLogo');
		if (data.avatar) {
			formData.append('files', data.avatar, 'avatar');
		} else if (data.avatarUrl) {
			formData.append('avatarUrl', data.avatarUrl);
		}

		return await this.api.patch<IStore>(
			`/store/${storeId}/chatbot-settings`,
			formData,
			{
				...config,
				headers: {
					'Content-Type': 'multipart/form-data',
					...(config?.headers || {}),
				},
			},
		);
	}

	async getStripeAccountStatus(
		config?: ApiRequestConfig,
	): Promise<IStripeAccountStatus> {
		return await this.api.get<IStripeAccountStatus>(
			`/stripe/account-status`,
			config,
		);
	}

	async checkExistingConnectAccount(
		email: string,
		config?: ApiRequestConfig,
	): Promise<any> {
		return await this.api.get<any>(
			`/stripe/check-existing-account?email=${encodeURIComponent(email)}`,
			config,
		);
	}

	async getStripeOnboardingLink(
		selectedCountry: string,
		config?: ApiRequestConfig,
	): Promise<{ url: string }> {
		return await this.api.get<{ url: string }>(
			`/stripe/onboarding-link?code=${selectedCountry}`,
			config,
		);
	}

	async createB2BPaymentIntent(
		amount: number,
		supplierId: number,
		config?: ApiRequestConfig,
	): Promise<{ clientSecret: string }> {
		return await this.api.post<{ clientSecret: string }>(
			`/stripe/b2b-payment-intent`,
			{ amount, supplierId },
			config,
		);
	}

	async createB2BCheckoutSession(
		amount: number,
		supplierId: number,
		orderId: number,
		storeName: string,
		discountCode: string,
		email: string,
		config?: ApiRequestConfig,
	): Promise<{ sessionId: string }> {
		return await this.api.post<{ sessionId: string }>(
			`/stripe/b2b-checkout-session`,
			{ amount, supplierId, orderId, storeName, discountCode, email },
			config,
		);
	}

	async checkIfVendorIsValid(
		email: string,
		config?: ApiRequestConfig,
	): Promise<IUser> {
		return await this.api.get<IUser>(`stripe/user/${email}`, config);
	}

	async verifyHagglePassword(
		haggleEventId: number,
		password: string,
	): Promise<boolean> {
		const response = await this.api.post<{ valid: boolean }>(
			'/haggle-event/verify-password',
			{ haggleEventId, password },
		);
		return response.valid;
	}

	async checkDiscountCode(
		code: string,
		productVariantId: string,
		config?: ApiRequestConfig,
	): Promise<{ sessionId: string }> {
		return await this.api.post<{ sessionId: string }>(
			`/stripe/check-discount-code`,
			{ code, productVariantId },
			config,
		);
	}

	async fetchSupportedStripeCountries(config?: ApiRequestConfig): Promise<any> {
		return await this.api.get<{ url: string }>(
			`/stripe/supported-countries`,
			config,
		);
	}

	async createOrder(
		orderData: ICreateOrder,
		config?: ApiRequestConfig,
	): Promise<IOrder> {
		return await this.api.post<IOrder>(`/orders`, orderData, config);
	}

	async getCheckoutSession(
		sessionId: string,
		config?: ApiRequestConfig,
	): Promise<{ payment_intent: string }> {
		return await this.api.get<{ payment_intent: string }>(
			`/stripe/checkout-session/${sessionId}`,
			config,
		);
	}

	async updateOrderPayment(
		orderId: number,
		paymentIntentId: string,
		storeName: string,
		isVendorPaidUser: boolean,
		config?: ApiRequestConfig,
	): Promise<IOrder> {
		const payload = {
			paymentIntentId,
			storeName,
			isVendorPaidUser,
		};

		return await this.api.patch<IOrder>(
			`/orders/${orderId}/payment`,
			payload,
			config,
		);
	}

	async createProduct(
		productData: ICreateProductPayload,
	): Promise<ICreateProductPayload> {
		const response = await this.api.post('/products', productData);
		return response as ICreateProductPayload;
	}

	async updateProduct(
		id: number | string,
		productData: ICreateProductPayload,
		config?: ApiRequestConfig,
	): Promise<ICreateProductPayload> {
		const response = await this.api.put(`/products/${id}`, productData, config);
		return response as ICreateProductPayload;
	}

	async deleteProduct(
		id: number | string,
		config?: ApiRequestConfig,
	): Promise<void> {
		return await this.api.delete(`/products/${id}`, config);
	}

	async uploadImage(image: File): Promise<string> {
		const formData = new FormData();
		formData.append('file', image);
		const response = await this.api.post<{ image: string }>(
			'/products/upload-image',
			formData,
		);
		return response.image;
	}

	async deactivateUser(
		payload: { isPromotionSubscribed: boolean },
		config?: ApiRequestConfig,
	): Promise<void> {
		return await this.api.post('/user/deactivate-user', payload, config);
	}

	async createPromotionalUser(
		payload: ICreatePromotionalUserPayload,
		config?: ApiRequestConfig,
	): Promise<IPromotionalUser> {
		return await this.api.post('/promotional-user', payload, config);
	}

	async syncStoreFromShopify(config?: ApiRequestConfig): Promise<IStore> {
		return await this.api.get<IStore>('/store/sync-shopify', config);
	}

	// Shopify Subscription Discounts
	async getAllShopifySubscriptionDiscounts(
		config?: ApiRequestConfig,
	): Promise<IShopifySubscriptionDiscount[]> {
		return await this.api.get<IShopifySubscriptionDiscount[]>(
			'/shopify-subscription-discounts',
			config,
		);
	}

	async getShopifySubscriptionDiscountById(
		id: string,
		config?: ApiRequestConfig,
	): Promise<IShopifySubscriptionDiscount> {
		return await this.api.get<IShopifySubscriptionDiscount>(
			`/shopify-subscription-discounts/${id}`,
			config,
		);
	}

	async createShopifySubscriptionDiscount(
		payload: ICreateShopifySubscriptionDiscountPayload,
		config?: ApiRequestConfig,
	): Promise<IShopifySubscriptionDiscount> {
		return await this.api.post<IShopifySubscriptionDiscount>(
			'/shopify-subscription-discounts',
			payload,
			config,
		);
	}

	async updateShopifySubscriptionDiscount(
		id: string,
		payload: IUpdateShopifySubscriptionDiscountPayload,
		config?: ApiRequestConfig,
	): Promise<IShopifySubscriptionDiscount> {
		return await this.api.put<IShopifySubscriptionDiscount>(
			`/shopify-subscription-discounts/${id}`,
			payload,
			config,
		);
	}

	async deleteShopifySubscriptionDiscount(
		id: string,
		config?: ApiRequestConfig,
	): Promise<void> {
		return await this.api.delete(
			`/shopify-subscription-discounts/${id}`,
			config,
		);
	}
}

export const haggleApiService = new HaggleApiService(apiService);
